import {
  CartItemFragment,
  GetCartQuery,
  UpdateCartMutation,
  EmptyCartMutation,
  UpdateCartWithMultipleProductsMutation,
  CartItemParamInput,
  CartItemResultFragment,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { GET_CART } from '@dustin-web/microsite-data/src/graphql/product/get-customer-product-lists.graphql'
import { UPDATE_CART } from '@dustin-web/microsite-data/src/graphql/product/update-cart.graphql'
import { UPDATE_CART_WITH_MULTIPLE_PRODUCTS } from '@dustin-web/microsite-data/src/graphql/product/update-cart-with-multipe-products.graphql'
import { flatten, getRowPrice } from '@dustin-web/microsite-data/src/utils'
import { useUserSettings } from '@dustin-web/microsite-components/src/hooks'
import { EMPTY_CART } from '@dustin-web/microsite-data/src/graphql/product/empty-cart'
import { useGraphql, useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import {
  makeVar,
  useReactiveVar,
} from '@dustin-web/microsite-components/src/hooks/use-reactive-var'
import { useCallback, useMemo, useRef } from 'react'
import debounce from 'lodash/debounce'
import { CartBaseProduct } from '@dustin-web/product-components/src/hooks/cart/cart-base-product-types'
import {
  getOptimisticResponse,
  upsertCartCacheWithNewItems,
} from '@dustin-web/product-components/src/hooks/cart/cache-updater-cart'
import {
  CartActionType,
  useAssistiveCartContext,
} from '@dustin-web/microsite-components/src/hooks/use-assistive-cart-context'

export type UseShoppingCart = typeof useShoppingCart
export type UpdateCart = ReturnType<typeof useShoppingCart>['updateCart']
export type UpdateCartWithMultipleProducts = ReturnType<
  typeof useShoppingCart
>['updateCartWithMultipleProducts']
export type EmptyCart = ReturnType<typeof useShoppingCart>['emptyCart']
export type CartProduct = NonNullable<CartItemFragment['product']>
export type UpdateCartWithMultipleProductParams = CartItemParamInput & { product: CartBaseProduct }

const lastUpdatedProductErpIdentifierVar = makeVar('')
const isUpdateVar = makeVar(false)

const filterAvailableCartItems = (cartItems: CartItemFragment[]) => {
  return cartItems.filter(x => x.product?.availability?.isAvailableForSale || x.isMandatory)
}

export type UseShoppingCartProps = {
  withRequiredEndUserInformation?: boolean
}

export const useShoppingCart = (options: UseShoppingCartProps = {}) => {
  const { withRequiredEndUserInformation = false } = options

  const { showPricesInclVat } = useUserSettings()
  const isUpdating = useReactiveVar(isUpdateVar)
  const lastUpdatedProductErpIdentifier = useReactiveVar(lastUpdatedProductErpIdentifierVar)

  const { setAssistiveCartData } = useAssistiveCartContext()

  const [updateCartMutation] = useGraphqlMutation<UpdateCartMutation>(UPDATE_CART)

  const [updateCartWithMultipleProductsMutation] =
    useGraphqlMutation<UpdateCartWithMultipleProductsMutation>(UPDATE_CART_WITH_MULTIPLE_PRODUCTS)

  const [emptyCartMutation] = useGraphqlMutation<EmptyCartMutation>(EMPTY_CART)

  const {
    data: cartData,
    error,
    isValidating: loading,
    mutate,
  } = useGraphql<GetCartQuery>(GET_CART, {
    context: { cacheKey: 'GetCart' },
    variables: {
      withRequiredEndUserInformation,
    },
  })

  const cartItemsCacheKey = JSON.stringify(cartData?.cart?.items) + loading
  const cartItems = useMemo(
    () => filterAvailableCartItems(cartData?.cart?.items || []),
    [cartItemsCacheKey]
  )

  const priceSummaries = useMemo(
    () =>
      cartItems.reduce(
        (acc, item) => {
          const totalPrice =
            acc.totalPrice +
            (item.product?.price
              ? getRowPrice(item.product.price, showPricesInclVat, item.quantity)
              : 0)
          const totalPriceExcludingVat =
            acc.totalPriceExcludingVat +
            (item.product?.price?.priceExcludingVat ?? 0) * item.quantity
          const totalPriceIncludingVat =
            acc.totalPriceIncludingVat +
            (item.product?.price?.priceIncludingVat ?? 0) * item.quantity

          const totalVat = totalPriceIncludingVat - totalPriceExcludingVat

          return {
            totalPrice,
            totalPriceExcludingVat,
            totalPriceIncludingVat,
            totalVat,
          }
        },
        { totalPrice: 0, totalPriceExcludingVat: 0, totalPriceIncludingVat: 0, totalVat: 0 }
      ),
    [cartItemsCacheKey]
  )

  const totalQuantity = useMemo(
    () => cartItems.reduce((acc, val) => acc + val.quantity, 0),
    [cartItems]
  )

  const hasPendingUpdate = useRef(false)

  const updateCartWithMultipleProducts = async (
    cartItemParams: UpdateCartWithMultipleProductParams[]
  ) => {
    const params: CartItemParamInput[] = cartItemParams.map(x => ({
      ...x,
      product: undefined,
    }))

    const optimisticResponseData = flatten(
      cartItemParams.map(x =>
        getOptimisticResponse({
          cartItems,
          productErpIdentifier: x.productErpIdentifier || '',
          mainProductErpIdentifier: x.mainProductErpIdentifier || null,
          quantity: x.quantity,
          product: x.product,
          comment: x.comment,
        })
      )
    )

    setAssistiveCartData({
      type: CartActionType.MULTIPLEPRODUCTS,
      actionData: { cartItems: cartItems, numberOfProducts: cartItemParams.length },
    })

    isUpdateVar(true)

    // Optimistic response
    mutate(updateCartCache(cartData?.cart, optimisticResponseData, true), false)

    await updateCartWithMultipleProductsMutation({
      variables: { cartItemParams: params, withRequiredEndUserInformation },
    }).catch(err => {
      isUpdateVar(false)
      throw err
    })

    isUpdateVar(false)

    // Refetch to ensure latest data
    mutate()
  }

  const debounces = {} as { [key: string]: any }

  const debouncedUpdateCartMutation = useCallback((key: string) => {
    const debounceFunc = debounces[key]

    return (
      debounceFunc ??
      (debounces[key] = debounce(async (variables?: any) => {
        hasPendingUpdate.current = false

        // @ts-ignore
        const response = await updateCartMutation({
          variables,
        }).catch(err => {
          isUpdateVar(false)
          throw err
        })

        // To avoid flickering ("Schrödingers") cart, only refresh if:
        // 1. we have no pending debounces for other items
        // 2. this debounce was not intermediately interrupted by a pending change
        delete debounces[key]
        if (!Object.keys(debounces).length && !hasPendingUpdate.current) {
          setAssistiveCartData({
            type: CartActionType.SINGLEPRODUCT,
            actionData: {
              cartItems: cartItems,
              product: {
                productErpIdentifier: variables.productErpIdentifier,
                displayName: variables.productName,
                newQuantity: variables.quantity,
              },
            },
          })

          isUpdateVar(false)
          mutate()
        }
      }, 500))
    )
  }, [])

  const updateCartCache = (
    cart: GetCartQuery['cart'],
    newData: CartItemResultFragment[],
    preventSetLastUpdatedProductErpIdentifier?: boolean
  ) => {
    const data = upsertCartCacheWithNewItems(cart, newData)

    if (!preventSetLastUpdatedProductErpIdentifier) {
      lastUpdatedProductErpIdentifierVar(
        newData
          .filter(p => !p.parentCartItemId && p.quantity > 0)
          .map(p => p.productErpIdentifier)[0]
      )
    }

    return data
  }

  const updateCart = async (
    product: CartBaseProduct,
    mainProductErpIdentifier: string | null,
    newQuantity: number,
    comment?: string | null,
    parentCartItemId?: number | null,
    preventSetLastUpdatedProductErpIdentifier?: boolean
  ) => {
    hasPendingUpdate.current = true
    isUpdateVar(true)

    const actualQuantity = newQuantity < 0 ? 0 : newQuantity

    const variables = {
      productErpIdentifier: product.productErpIdentifier,
      mainProductErpIdentifier,
      quantity: actualQuantity,
      comment,
      withRequiredEndUserInformation,
      parentCartItemId: parentCartItemId || null,
      preventSetLastUpdatedProductErpIdentifier,
      productName: product.displayName,
    }

    const optimisticResponseData = getOptimisticResponse({
      cartItems,
      ...variables,
      product,
    })

    // Optimistic response
    mutate(
      updateCartCache(
        cartData?.cart,
        optimisticResponseData,
        preventSetLastUpdatedProductErpIdentifier
      ),
      false
    )

    await debouncedUpdateCartMutation(product.productErpIdentifier)(variables)
  }

  const emptyCart = () => {
    const cartWithoutItems = {
      cart: {
        id: cartData?.cart?.id || 0,
        items: [],
      },
    }

    setAssistiveCartData({
      type: CartActionType.CLEARCART,
      actionData: {},
    })

    mutate(
      async () => {
        await emptyCartMutation()
        return cartWithoutItems
      },
      { revalidate: true, optimisticData: cartWithoutItems, populateCache: false }
    )
  }

  /** Get the quantity of a product in the cart. Returns 0 if product is not in cart */
  const quantityOfProductInCart = (productErpIdentifier: string): number => {
    return (
      cartItems
        .filter(i => i?.product?.productErpIdentifier === productErpIdentifier)
        .reduce((a, b) => a + b.quantity, 0) || 0
    )
  }

  return {
    loading,
    error,
    cartItems,
    quantityOfProductInCart,
    updateCart,
    updateCartWithMultipleProducts,
    emptyCart,
    totalQuantity,
    lastUpdatedProductErpIdentifier,
    isUpdating,
    refetchCart: mutate,
    suggestedDelivery: cartData?.suggestedDelivery,
    ...priceSummaries,
  }
}

export default useShoppingCart
